import React, { Component } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { fetchFiftyFiftyEvent, fetchFiftyFiftyList } from "./fiftyFiftyApi";
import { resolveMediaUrl } from "../Helper/mediaUrl";
import "./FiftyFiftyEventPage.css";

/**
 * Public Monthly 50/50 presentation — list + detail.
 * No live purchase gateway (Product/Legal gate; admin-recorded entries only).
 */

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
}

function formatMoney(amount, currency) {
  if (amount == null || amount === "") return "—";
  const cur = currency || "USD";
  const n = typeof amount === "number" ? amount : parseFloat(amount);
  if (Number.isNaN(n)) return `${amount} ${cur}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
    }).format(n);
  } catch (e) {
    return `${n.toFixed(2)} ${cur}`;
  }
}

/** Neutral copy when jurisdiction is not enabled — do not claim illegality. */
const UNAVAILABLE_IN_AREA =
  "This event is not currently available in your area.";

function stateAvailability(event) {
  const sa = (event && event.state_availability) || {};
  const hasAny =
    sa.has_any_approved_state === true ||
    (Array.isArray(event && event.approved_states) &&
      event.approved_states.length > 0);
  const message =
    (typeof sa.unavailable_message === "string" && sa.unavailable_message) ||
    UNAVAILABLE_IN_AREA;
  return { hasAny, message };
}

class FiftyFiftyEventPage extends Component {
  state = {
    loading: true,
    event: null,
    list: [],
    error: false,
    errorMessage: "",
    notFound: false,
  };

  loadGeneration = 0;

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const prevSlug =
      prevProps.match && prevProps.match.params
        ? prevProps.match.params.slug
        : "";
    const nextSlug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    if (prevSlug !== nextSlug) {
      this.load();
    }
  }

  slug() {
    return this.props.match && this.props.match.params
      ? this.props.match.params.slug
      : "";
  }

  async load() {
    const slug = this.slug();
    const generation = ++this.loadGeneration;
    this.setState({
      loading: true,
      error: false,
      errorMessage: "",
      notFound: false,
    });
    if (!slug) {
      const result = await fetchFiftyFiftyList();
      if (generation !== this.loadGeneration) {
        return;
      }
      this.setState({
        loading: false,
        list: result.events || [],
        event: null,
        error: !!result.error,
        errorMessage: result.message || "",
        notFound: false,
      });
      return;
    }
    const result = await fetchFiftyFiftyEvent(slug);
    if (generation !== this.loadGeneration) {
      return;
    }
    if (result.event) {
      this.setState({
        loading: false,
        event: result.event,
        error: false,
        notFound: false,
        errorMessage: "",
        list: [],
      });
      return;
    }
    const isNotFound =
      !result.error || /not found/i.test(String(result.message || ""));
    this.setState({
      loading: false,
      event: null,
      error: !isNotFound,
      notFound: isNotFound,
      errorMessage: result.message || "",
      list: [],
    });
  }

  renderAvailabilityCopy(event) {
    const { hasAny, message } = stateAvailability(event);
    // Default deny: empty approved set → nobody purchasable (neutral copy).
    if (!hasAny) {
      return <p className="ff5050-availability">{message}</p>;
    }
    if (event.accepts_entries) {
      return (
        <p>
          Entries are open for areas configured for this event. Online card
          purchase is not enabled until Product/Legal clearance — contact
          Pentecost+ / Admin for entry instructions. If this event is not
          available where you are, you will see: “{message}”
        </p>
      );
    }
    return <p>Entries are not currently being accepted for this event.</p>;
  }

  renderList() {
    const { list, error, errorMessage } = this.state;
    return (
      <div className="ff5050-page">
        <Helmet>
          <title>Monthly 50/50 · Pentecost+</title>
        </Helmet>
        <header className="ff5050-header">
          <p className="ff5050-brand">Pentecost+</p>
          <h1 className="ff5050-title">Monthly 50/50</h1>
          <p className="ff5050-lede">
            50% to the winner · 20% to Pentecost+ operations · 30% to a selected
            Organization or Project.
          </p>
        </header>

        {error ? (
          <div className="ff5050-alert" role="alert">
            <p>{errorMessage || "We couldn’t load 50/50 events."}</p>
            <button
              type="button"
              className="ff5050-retry"
              onClick={() => this.load()}
            >
              Retry
            </button>
          </div>
        ) : null}

        <ul className="ff5050-list">
          {!error && list.length === 0 ? (
            <li className="ff5050-empty">No public events right now.</li>
          ) : (
            list.map((item) => (
              <li key={item.uuid || item.id} className="ff5050-list-item">
                <div className="ff5050-list-main">
                  <Link
                    className="ff5050-list-link"
                    to={`/fifty-fifty/${item.slug}`}
                  >
                    {item.name}
                  </Link>
                  <span className="ff5050-meta">
                    {formatMoney(item.ticket_price, item.currency)}
                    {item.accepts_entries ? " · Entries open" : ""}
                  </span>
                </div>
                <span
                  className={
                    "ff5050-badge" +
                    (item.accepts_entries ? " ff5050-badge--open" : "")
                  }
                >
                  {item.status_label}
                </span>
              </li>
            ))
          )}
        </ul>
      </div>
    );
  }

  renderEvent() {
    const { event } = this.state;
    const beneficiary = event.beneficiary;
    const image = event.image_path
      ? resolveMediaUrl(event.image_path)
      : null;
    const currency = event.currency || "USD";

    return (
      <div className="ff5050-page">
        <Helmet>
          <title>{event.name} · Monthly 50/50</title>
        </Helmet>
        {image ? (
          <div
            className="ff5050-hero"
            style={{ backgroundImage: `url(${image})` }}
            role="img"
            aria-label={event.name}
          />
        ) : null}
        <header className="ff5050-header">
          <p className="ff5050-brand">Pentecost+</p>
          <p
            className={
              "ff5050-status" +
              (event.accepts_entries ? " ff5050-status--open" : "")
            }
          >
            {event.status_label}
          </p>
          <h1 className="ff5050-title">{event.name}</h1>
          {event.description ? (
            <p className="ff5050-lede">{event.description}</p>
          ) : null}
        </header>

        <section className="ff5050-section">
          <h2>Event details</h2>
          <dl className="ff5050-dl">
            <div>
              <dt>Starts</dt>
              <dd>{formatWhen(event.starts_at)}</dd>
            </div>
            <div>
              <dt>Ends</dt>
              <dd>{formatWhen(event.ends_at)}</dd>
            </div>
            <div>
              <dt>Entry price</dt>
              <dd>{formatMoney(event.ticket_price, currency)}</dd>
            </div>
            <div>
              <dt>Eligible entries</dt>
              <dd>{event.eligible_entry_count}</dd>
            </div>
            <div>
              <dt>Eligible proceeds</dt>
              <dd>{formatMoney(event.eligible_proceeds, currency)}</dd>
            </div>
          </dl>
        </section>

        <section className="ff5050-section">
          <h2>Allocations</h2>
          <ul className="ff5050-alloc">
            <li>
              Winner (50%):{" "}
              {formatMoney(
                event.allocations && event.allocations.winner,
                currency
              )}
            </li>
            <li>
              Pentecost+ operations (20%):{" "}
              {formatMoney(
                event.allocations && event.allocations.operations,
                currency
              )}
            </li>
            <li>
              Beneficiary (30%):{" "}
              {formatMoney(
                event.allocations && event.allocations.beneficiary,
                currency
              )}
            </li>
          </ul>
          {beneficiary ? (
            <p>
              30% supports{" "}
              <strong>
                {beneficiary.name} ({beneficiary.type})
              </strong>
              {beneficiary.slug ? (
                <>
                  {" "}
                  —{" "}
                  <Link to={`/${beneficiary.type}/${beneficiary.slug}`}>
                    View page
                  </Link>
                </>
              ) : null}
            </p>
          ) : (
            <p>Beneficiary will be announced by the Administrator.</p>
          )}
        </section>

        {event.winner ? (
          <section className="ff5050-section">
            <h2>Winner</h2>
            <p>
              Winning ticket #{event.winner.ticket_number} · Amount{" "}
              {formatMoney(event.winner.winning_amount, currency)}
            </p>
            {event.winner.selected_at ? (
              <p className="ff5050-meta">
                Selected {formatWhen(event.winner.selected_at)}
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="ff5050-section">
          <h2>Terms &amp; eligibility</h2>
          {event.terms_text ? (
            <p className="ff5050-terms">{event.terms_text}</p>
          ) : (
            <p className="ff5050-terms">Terms have not been published for this event.</p>
          )}
          {event.legal_document_key ? (
            <p>
              <Link to={`/legal/${event.legal_document_key}`}>
                View related legal document
              </Link>
            </p>
          ) : (
            <p>
              <Link to="/terms">Platform terms</Link>
            </p>
          )}
          {event.compliance_notice ? (
            <p className="ff5050-compliance">{event.compliance_notice}</p>
          ) : null}
        </section>

        <section className="ff5050-section ff5050-cta">
          {this.renderAvailabilityCopy(event)}
          <Link className="ff5050-back" to="/fifty-fifty">
            All 50/50 events
          </Link>
        </section>
      </div>
    );
  }

  renderDetailFailure() {
    const { error, notFound, errorMessage } = this.state;
    const title = notFound ? "Event not found" : "Unable to load event";
    const helmetTitle = notFound
      ? "Event not found · Monthly 50/50"
      : "Unable to load · Monthly 50/50";
    return (
      <div className="ff5050-page">
        <Helmet>
          <title>{helmetTitle}</title>
        </Helmet>
        <h1 className="ff5050-title">{title}</h1>
        <p className="ff5050-lede" role={error ? "alert" : undefined}>
          {errorMessage ||
            (notFound
              ? "This 50/50 event isn’t available."
              : "We couldn’t load this 50/50 event. Please try again.")}
        </p>
        {error || notFound ? (
          <button
            type="button"
            className="ff5050-retry"
            onClick={() => this.load()}
          >
            Retry
          </button>
        ) : null}{" "}
        <Link className="ff5050-back" to="/fifty-fifty">
          Back to 50/50
        </Link>
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="ff5050-page ff5050-loading" role="status">
          <Helmet>
            <title>Loading · Monthly 50/50</title>
          </Helmet>
          Loading…
        </div>
      );
    }
    if (!this.slug()) {
      return this.renderList();
    }
    if (this.state.error || this.state.notFound || !this.state.event) {
      return this.renderDetailFailure();
    }
    return this.renderEvent();
  }
}

export default FiftyFiftyEventPage;
